import styles from "./EditarLoja.module.scss";
import React, { useEffect, useRef, useState } from "react";
import { Store } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "shared/components/pagecontainer/PageContainer";
import { buscarLojaPorId, atualizarLoja, AtualizarLojaPayload } from "shared/services/api/lojas.api";
import { ILoja } from "shared/interfaces/ILoja";
import { IConta } from "shared/interfaces/IConta";
import { DadosLoja, BusinessHoursSchedule } from "shared/components/etapasCadastro/Loja/DadosLoja";
import { RepresentanteLegal } from "shared/components/etapasCadastro/Loja/RepresentanteLegal";
import { Endereco, EnderecoLojaFormData } from "shared/components/etapasCadastro/Loja/Endereco";
import { DadosBancarios, DadosBancariosFormData } from "shared/components/etapasCadastro/Loja/DadosBancarios";
import { traduzirErro } from "shared/utils/traduzirErro";

type Aba = "informacoes" | "representante" | "endereco" | "bancarios";

export const EditarLoja: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [abaAtual, setAbaAtual] = useState<Aba>("informacoes");
  const [loja, setLoja] = useState<ILoja | null>(null);
  const [dadosBancarios, setDadosBancarios] = useState<IConta | null>(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const tabContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const carregarDados = async () => {
      if (!id || id === "undefined") {
        navigate("/minhaslojas", { replace: true });
        return;
      }

      try {
        setLoading(true);
        const dadosLoja = await buscarLojaPorId(id);

        if (!dadosLoja) {
          setLoja(null);
          setDadosBancarios(null);
          return;
        }

        setLoja(dadosLoja);
        setDadosBancarios(dadosLoja.dadosBancarios ?? null);
      } catch {
        setLoja(null);
        setDadosBancarios(null);
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [id, navigate]);

  


  const handleSalvarInformacoes = async (dados: any) => {
    if (!id) return;

    try {
      setSalvando(true);
      setErro(null);

      console.log("=== DEBUG EDITAR LOJA ===");
      console.log("Dados completos:", dados);
      console.log("Ganho anual do formulário (reais):", dados.annual_revenue);
      console.log("Tipo do ganho anual:", typeof dados.annual_revenue);
      console.log(
        "Ganho anual em centavos (enviado à API):",
        Math.round(dados.annual_revenue * 100)
      );

      const businessHoursSchedule: BusinessHoursSchedule = {
        MON: dados.businessHoursSchedule?.MON ?? null,
        TUE: dados.businessHoursSchedule?.TUE ?? null,
        WED: dados.businessHoursSchedule?.WED ?? null,
        THU: dados.businessHoursSchedule?.THU ?? null,
        FRI: dados.businessHoursSchedule?.FRI ?? null,
        SAT: dados.businessHoursSchedule?.SAT ?? null,
        SUN: dados.businessHoursSchedule?.SUN ?? null,
      };

      const payload: AtualizarLojaPayload = {
        name: dados.loja?.trim(),
        companyName: dados.company_name?.trim(),
        phone: dados.telefone?.replace(/\D/g, ""),
        email: dados.emailLoja ?? dados.email,
        description: dados.descricao?.trim(),
        categoryId: dados.segmento,
        annualRevenue: Math.round(dados.annual_revenue * 100),
        businessHours: businessHoursSchedule,
      };

      console.log("Payload enviado para atualizar loja:", payload);
      console.log("Ganho anual no payload:", payload.annualRevenue);

      await atualizarLoja(id, payload);

      console.log("Atualização concluída com sucesso");

      const lojaAtualizada = await buscarLojaPorId(id);

      console.log("Loja retornada após atualizar:", lojaAtualizada);
      console.log(
        "Ganho anual retornado pela API:",
        lojaAtualizada?.annual_revenue
      );

      if (!lojaAtualizada) {
        throw new Error(
          "A loja foi atualizada, mas não foi possível recarregar os dados."
        );
      }

      setLoja(lojaAtualizada);
      setAbaAtual("representante");
    } catch (error: any) {
      console.error("Erro ao salvar informações da loja:", error);
      console.error(
        "Mensagem detalhada da API:",
        JSON.stringify(error?.response?.data?.message, null, 2)
      );
      console.error(
        "Resposta completa da API:",
        JSON.stringify(error?.response?.data, null, 2)
      );

      setErro(traduzirErro(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleSalvarRepresentante = async (
    dados: {
      mother_name: string;
      monthly_income: number;
      professional_occupation: string;
      self_declared_legal_representative: boolean;
      addressId: string;
    }
  ) => {
    if (!id) return;

    try {
      setSalvando(true);
      setErro(null);

      const payload: AtualizarLojaPayload = {
        legalRepresentative: {
          addressId: dados.addressId,
          motherName: dados.mother_name.trim(),
          monthlyIncome: Math.round(dados.monthly_income * 100),
          professionalOccupation: dados.professional_occupation.trim(),
          selfDeclaredLegalRepresentative: dados.self_declared_legal_representative,
        },
      };

      await atualizarLoja(id, payload);

      const lojaAtualizada = await buscarLojaPorId(id);

      if (!lojaAtualizada) {
        throw new Error("A loja foi atualizada, mas não foi possível recarregar os dados.");
      }

      setLoja(lojaAtualizada);
      setAbaAtual("endereco");
    } catch (error: any) {
      setErro(traduzirErro(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleSalvarEndereco = async (dados: EnderecoLojaFormData) => {
    if (!id) return;

    try {
      setSalvando(true);
      setErro(null);

      const endereco = {
        street: dados.endereco.trim(),
        number: dados.numero.trim(),
        neighborhood: dados.bairro.trim(),
        city: dados.cidade.trim(),
        state: dados.estado.trim(),
        zipCode: dados.cep.replace(/\D/g, ""),
        complement: dados.completo?.trim() ?? "",
        referencePoint: dados.pontoReferencia?.trim() ?? "",
      };

      await atualizarLoja(id, {
        address: endereco,
      });

      const lojaAtualizada = await buscarLojaPorId(id);

      if (!lojaAtualizada) {
        throw new Error("A loja foi atualizada, mas não foi possível recarregar os dados.");
      }

      setLoja(lojaAtualizada);
      setAbaAtual("bancarios");
    } catch (error: any) {
      setErro(traduzirErro(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleSalvarBancarios = async (dados: DadosBancariosFormData) => {
    if (!id) return;

    try {
      setSalvando(true);
      setErro(null);

      const agenciaPartes = dados.agencia.trim().split("-");
      const contaPartes = dados.conta.trim().toUpperCase().split("-");

      const banking = {
        bankCode: String(dados.banco).replace(/\D/g, "").padStart(3, "0"),
        branch: agenciaPartes[0] ?? "",
        branchDigit: agenciaPartes[1] ?? null,
        account: contaPartes[0] ?? "",
        accountDigit: contaPartes[1] ?? "",
        type: dados.tipoConta === "poupanca" ? ("savings" as const) : ("checking" as const),
      };

      const payload: AtualizarLojaPayload = {
        banking,
      };

      await atualizarLoja(id, payload);

      const lojaAtualizada = await buscarLojaPorId(id);

      if (lojaAtualizada) {
        setLoja(lojaAtualizada);
        setDadosBancarios(lojaAtualizada.dadosBancarios ?? null);
      }

      navigate("/minhaslojas");
    } catch (error: any) {
      setErro(traduzirErro(error));
    } finally {
      setSalvando(false);
    }
  };

  const submeterFormularioAtual = () => {
    if (salvando) return;

    const form = tabContentRef.current?.querySelector<HTMLFormElement>("form");

    if (!form) return;

    form.requestSubmit();
  };

  const voltar = () => {
    if (salvando) return;

    setErro(null);

    if (abaAtual === "informacoes") {
      navigate("/minhaslojas");
      return;
    }

    if (abaAtual === "representante") {
      setAbaAtual("informacoes");
      return;
    }

    if (abaAtual === "endereco") {
      setAbaAtual("representante");
      return;
    }

    setAbaAtual("endereco");
  };

  const avancar = () => {
    if (salvando) return;
    submeterFormularioAtual();
  };

  if (loading) {
    return (
      <PageContainer title="Editar Loja" showDesktopSearch={false} icon={<Store size={18} />}>
        <div className={styles.loading}>Carregando dados da loja...</div>
      </PageContainer>
    );
  }

  if (!loja) {
    return (
      <PageContainer title="Editar Loja" showDesktopSearch={false} icon={<Store size={18} />}>
        <div className={styles.empty}>
          <h2>Loja não encontrada</h2>
          <p>Não foi possível localizar esta loja.</p>
          <button type="button" className={styles.btnVoltar} onClick={() => navigate("/minhaslojas")}>
            Voltar para lojas
          </button>
        </div>
      </PageContainer>
    );
  }

  const businessHoursSchedule: BusinessHoursSchedule = {
    MON: loja.businessHoursSchedule?.MON ?? null,
    TUE: loja.businessHoursSchedule?.TUE ?? null,
    WED: loja.businessHoursSchedule?.WED ?? null,
    THU: loja.businessHoursSchedule?.THU ?? null,
    FRI: loja.businessHoursSchedule?.FRI ?? null,
    SAT: loja.businessHoursSchedule?.SAT ?? null,
    SUN: loja.businessHoursSchedule?.SUN ?? null,
  };

  const dadosBancariosDefaultValues = {
    banco: dadosBancarios?.banco?.codigo ?? "",
    agencia: dadosBancarios?.agencia ?? "",
    conta: dadosBancarios?.numeroConta ?? "",
    tipoConta: dadosBancarios?.tipoConta ?? "",
  };

  return (
    <PageContainer title={`Editar ${loja.loja ?? "Loja"}`} showDesktopSearch={false} icon={<Store size={18} />}>
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.formHeader}>
            <div>
              <h2>Editar loja</h2>
              <p>Atualize as informações da sua loja.</p>
            </div>
          </div>

          <div className={styles.breadcrumbs}>
            <button
              type="button"
              className={`${styles.breadcrumb} ${abaAtual === "informacoes" ? styles.breadcrumbAtivo : styles.breadcrumbConcluido}`}
              onClick={() => {
                setErro(null);
                setAbaAtual("informacoes");
              }}
              disabled={salvando}
            >
              Informações
            </button>

            <span className={styles.separador}>/</span>

            <button
              type="button"
              className={`${styles.breadcrumb} ${abaAtual === "representante" ? styles.breadcrumbAtivo : abaAtual === "endereco" || abaAtual === "bancarios" ? styles.breadcrumbConcluido : ""}`}
              onClick={() => {
                setErro(null);
                setAbaAtual("representante");
              }}
              disabled={salvando}
            >
              Representante Legal
            </button>

            <span className={styles.separador}>/</span>

            <button
              type="button"
              className={`${styles.breadcrumb} ${abaAtual === "endereco" ? styles.breadcrumbAtivo : abaAtual === "bancarios" ? styles.breadcrumbConcluido : ""}`}
              onClick={() => {
                setErro(null);
                setAbaAtual("endereco");
              }}
              disabled={salvando}
            >
              Endereço
            </button>

            <span className={styles.separador}>/</span>

            <button
              type="button"
              className={`${styles.breadcrumb} ${abaAtual === "bancarios" ? styles.breadcrumbAtivo : ""}`}
              onClick={() => {
                setErro(null);
                setAbaAtual("bancarios");
              }}
              disabled={salvando}
            >
              Dados Bancários
            </button>
          </div>

          {erro && <div className={styles.error}>{erro}</div>}

          <div ref={tabContentRef} className={styles.tabContent}>
            {abaAtual === "informacoes" && (
              <DadosLoja
                modoEdicao
                lojaId={id}
                defaultValues={{
                  loja: loja.loja ?? "",
                  company_name: loja.company_name ?? "",
                  segmento: loja.segmento ?? "",
                  cnpj: loja.cnpj ?? "",
                  telefone: loja.telefone ?? "",
                  email: loja.emailLoja ?? "",
                  annual_revenue: loja.annual_revenue ?? 0,
                  descricao: loja.descricao ?? "",
                  businessHoursSchedule,
                }}
                onSalvo={handleSalvarInformacoes}
              />
            )}

            {abaAtual === "representante" && (
              <RepresentanteLegal
                modoEdicao
                lojaId={id}
                defaultValues={{
                  mother_name: loja.legalRepresentative?.motherName ?? loja.mother_name ?? "",
                  monthly_income: loja.legalRepresentative?.monthlyIncome ?? loja.monthly_income ?? 0,
                  professional_occupation: loja.legalRepresentative?.professionalOccupation ?? loja.professional_occupation ?? "",
                  self_declared_legal_representative:
                    loja.legalRepresentative?.selfDeclaredLegalRepresentative ??
                    loja.self_declared_legal_representative ??
                    false,
                  addressId: loja.legalRepresentative?.addressId ?? "",
                }}
                onSalvo={handleSalvarRepresentante}
              />
            )}

            {abaAtual === "endereco" && (
              <Endereco
                modoEdicao
                lojaId={id}
                defaultValues={{
                  cep: loja.endereco?.cep ?? "",
                  cidade: loja.endereco?.cidade ?? "",
                  estado: loja.endereco?.estado ?? "",
                  endereco: loja.endereco?.rua ?? "",
                  numero: loja.endereco?.numero ?? "",
                  bairro: loja.endereco?.bairro ?? "",
                  completo: loja.endereco?.complement ?? "",
                  pontoReferencia: loja.endereco?.referencePoint ?? "",
                }}
                onSalvo={handleSalvarEndereco}
              />
            )}

            {abaAtual === "bancarios" && (
              <DadosBancarios
                modoEdicao
                defaultValues={dadosBancariosDefaultValues}
                onSalvo={handleSalvarBancarios}
              />
            )}
          </div>

          <div className={styles.navigation}>
            <button
              type="button"
              className={styles.btnCancelar}
              onClick={voltar}
              disabled={salvando}
            >
              {abaAtual === "informacoes" ? "Cancelar" : "Voltar"}
            </button>

            <div className={styles.actionsRight}>
              {abaAtual !== "bancarios" && (
                <button
                  type="button"
                  className={styles.btnProximo}
                  onClick={avancar}
                  disabled={salvando}
                >
                  {salvando ? "Salvando..." : "Próximo"}
                </button>
              )}

              {abaAtual === "bancarios" && (
                <button
                  type="submit"
                  form="dados-bancarios-loja-form"
                  className={styles.btnSalvar}
                  disabled={salvando}
                >
                  {salvando ? "Salvando..." : "Concluir"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};